// Drag a lesson card to a new day/time (DayTimeGrid.vue). State is module-level because
// a drag crosses grid instances: each day (Week view) or teacher (Day view) column is its
// own DayTimeGrid, and whichever column is under the pointer draws the drop preview.
// A drop never moves anything by itself — it opens a confirm popover in the target
// column, and the move goes through the same /lesson-edits endpoint as the Edit modal
// (so the family gets the same batched notice, utils/schedule_notices.py).
import { ref } from 'vue'
import { useApi } from './useApi.js'
import { durationToH, parseLessonTime, yToTime } from './useCalendarData.js'

// { lesson, target: { key, day, teacher, time, duration? } | null } while a drag is active
// (a resize — the card's bottom-edge grip — stays in its own column and sets `duration`)
export const lessonDrag = ref(null)
// { lesson, key, day, teacher, time, duration? } after a drop, until confirmed or cancelled
export const dropPrompt = ref(null)

// The lengths /lesson-edits accepts (lesson_edits.ALLOWED_DURATIONS).
export const DURATIONS = [30, 45, 60, 90]

const MOUSE_SLOP = 5      // px before a mouse press becomes a drag (below it, it's a click)
const TOUCH_SLOP = 8      // px of finger movement that means "scrolling", not a long-press
const LONG_PRESS_MS = 400

let press = null

const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })

function targetAt(x, y) {
  const el = document.elementsFromPoint(x, y).find(n => n.dataset?.dropKey)
  if (!el) return null
  const rect = el.getBoundingClientRect()
  const { dropKey: key, dropDay: day, dropTeacher: teacher, minHour } = el.dataset
  // Another teacher's column would be a teacher change, which a move can't do.
  if (day < todayIso() || (teacher && teacher !== press.lesson.teacher_name)) return null
  return { key, day, teacher: teacher || null, time: yToTime(y - rect.top - press.grab, Number(minHour)) }
}

// Nearest allowed length to where the pointer is, never past `max` (the next lesson).
function resizeTarget(y) {
  const want = (y - press.cardTop) / press.pxPerMin
  const fits = DURATIONS.filter(d => d <= press.resize.max)
  const duration = fits.reduce((best, d) => Math.abs(d - want) < Math.abs(best - want) ? d : best, fits[0])
  return { ...press.resize, duration }
}

function onMove(e) {
  const dist = Math.hypot(e.clientX - press.x, e.clientY - press.y)
  if (!lessonDrag.value) {
    if (press.touch && !press.resize) { if (dist > TOUCH_SLOP) end() }   // the finger is scrolling
    else if (dist > MOUSE_SLOP) lessonDrag.value = { lesson: press.lesson, target: null }
    if (!lessonDrag.value) return
  }
  lessonDrag.value = { ...lessonDrag.value, target: press.resize ? resizeTarget(e.clientY) : targetAt(e.clientX, e.clientY) }
}

// While a touch drag is active, the page mustn't scroll under the finger.
function onTouchMove(e) { if (lessonDrag.value) e.preventDefault() }

function swallowNextClick() {
  const stop = e => { e.stopPropagation(); e.preventDefault() }
  window.addEventListener('click', stop, { capture: true, once: true })
  setTimeout(() => window.removeEventListener('click', stop, { capture: true }), 300)
}

function onUp() {
  const drag = lessonDrag.value
  end()
  if (!drag) return
  swallowNextClick()   // releasing over the card would otherwise open its details modal
  const t = drag.target
  const p = drag.lesson
  if (!t) return
  if (t.duration) {
    if (t.duration === (p.duration_minutes || 30)) return
  } else {
    const [h, m] = t.time.split(':').map(Number)
    const old = parseLessonTime(p.lesson_time)
    if (t.day === p.lesson_date && old?.h === h && old?.m === m) return
  }
  dropPrompt.value = { lesson: p, ...t }
}

function end() {
  clearTimeout(press?.timer)
  press = null
  lessonDrag.value = null
  document.removeEventListener('pointermove', onMove)
  document.removeEventListener('pointerup', onUp)
  document.removeEventListener('pointercancel', end)
  document.removeEventListener('touchmove', onTouchMove)
}

export function startLessonPress(lesson, e) {
  if (e.button > 0) return
  end()
  dropPrompt.value = null
  const rect = e.currentTarget.getBoundingClientRect()
  press = { lesson, x: e.clientX, y: e.clientY, grab: e.clientY - rect.top, touch: e.pointerType === 'touch' }
  listen()
  if (press.touch) {
    press.timer = setTimeout(() => {
      if (!press) return
      lessonDrag.value = { lesson, target: null }
      navigator.vibrate?.(10)
    }, LONG_PRESS_MS)
  }
}

// Bottom-edge grip: drag to change the length. `col` = { key, day, teacher, time } of the
// lesson's own column, `max` = minutes until the teacher's next lesson. The grip is
// touch-action:none, so on a phone it resizes straight away (no long-press).
export function startLessonResize(lesson, e, col, max) {
  if (e.button > 0) return
  e.stopPropagation()   // not a move of the card underneath
  end()
  dropPrompt.value = null
  const card = e.currentTarget.parentElement.getBoundingClientRect()
  press = {
    lesson, x: e.clientX, y: e.clientY, touch: false, cardTop: card.top,
    pxPerMin: durationToH(60) / 60, resize: { ...col, max },
  }
  listen()
}

function listen() {
  document.addEventListener('pointermove', onMove)
  document.addEventListener('pointerup', onUp)
  document.addEventListener('pointercancel', end)
  document.addEventListener('touchmove', onTouchMove, { passive: false })
}

export const hhmmToAmPm = hhmm => {
  const [h, m] = hhmm.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`
}

// Weekday for the Edit modal's series form: 0=Sun…6=Sat.
const weekdayOf = iso => new Date(iso + 'T00:00:00').getDay()

export async function confirmDrop(allFuture) {
  const d = dropPrompt.value
  const { editLesson } = useApi()
  if (d.duration) {
    return editLesson(d.lesson.id, allFuture
      ? { lesson_only: false, duration_minutes: d.duration, effective_date: d.lesson.lesson_date }
      : { lesson_only: true, duration_minutes: d.duration })
  }
  const start_time = hhmmToAmPm(d.time)
  const body = allFuture
    ? {
        lesson_only: false, start_time, weekday: weekdayOf(d.day),
        // "This and following" starts at whichever of the old/new dates comes first.
        effective_date: [d.lesson.lesson_date, d.day].sort()[0],
      }
    : { lesson_only: true, start_time, ...(d.day !== d.lesson.lesson_date ? { date: d.day } : {}) }
  return editLesson(d.lesson.id, body)
}
